// @flow

import React, {useState, useEffect, useRef} from 'react';
import {DraggableCore} from 'react-draggable';

import {toggleDocumentResizeClassName} from './utils/toggleDocumentResizeClassName';
import {TOGGLE_CLASS_ACTIONS} from './constants';

import type {ComponentType, Node} from 'react';

import type {DraggableData} from 'react-draggable';
import type {Column, DataTableResizeState} from './typedef';


type Props<DataColumn = {||}> = {|
    width: number,
    handle: string,
    children: (DataTableResizeState, nodeRef: {current: ?HTMLDivElement}) => Node,
    minWidth?: number,
    maxWidth?: number,
    onStartResize: () => void,
    onStopResize: (state: DataTableResizeState) => void,
    onResize: (state: DataTableResizeState, column: Column<DataColumn>, key: number) => void,
|};

type ResizeProps = {| minWidth?: number, maxWidth?: number |};

export const handleResize = (resizedWidth: number, {minWidth, maxWidth}: ResizeProps): boolean =>
    (!maxWidth
        ? resizedWidth >= Number(minWidth)
        : resizedWidth >= Number(minWidth) && resizedWidth <= Number(maxWidth)
    );

export const DataTableResize: ComponentType<Props<Object>> = <DataColumn>(props: Props<DataColumn>) => {
    const {
        width: initialWidth,
        handle,
        children,
        minWidth = 75,
        maxWidth = 0,
        onStartResize = () => undefined,
        onStopResize,
    } = props;

    const [resizeState, setResizeState] = useState<DataTableResizeState>({
        width: initialWidth,
        deltaX: 0,
        resize: null
    });

    /**
     * The variable `deltaCache` is necessary for collecting cursor shifting values in column resize mode
     * after column width reaches its min value.
     * It will provide needs to compensate excessive movement during the cursor moving in reverse direction.
     */
    const deltaCache = useRef(0);
    const nodeRef = useRef<?HTMLDivElement>(null);

    const triggerResizeEventOnWindow = () => {
        const event = new Event('resize', {bubbles: true, cancelable: false});
        document.dispatchEvent(event);
    };

    const setStopResizeState = () => {
        setResizeState({
            ...resizeState,
            deltaX: 0,
            resize: false
        });
    };

    const onComponentWillUnmount = () => {
        toggleDocumentResizeClassName(TOGGLE_CLASS_ACTIONS.remove);
        if (resizeState.resize) {
            setStopResizeState();
        }
    };

    useEffect(() => {
        return () => {
            onComponentWillUnmount();
        };
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (!resizeState.resize && resizeState.resize !== null) {
            triggerResizeEventOnWindow();
            onStopResize(resizeState);
        }
    }, [resizeState.resize]); // eslint-disable-line react-hooks/exhaustive-deps

    const onStart = (e: Event) => {
        e.stopPropagation();
        if (resizeState.resize) {
            e.preventDefault();
            setStopResizeState();
            return;
        }
        setResizeState({
            ...resizeState,
            resize: true
        });
        onStartResize();
    };

    const handleDrag = (e: Event, data: DraggableData) => {
        const {deltaX: deltaOnResize} = data;
        toggleDocumentResizeClassName(TOGGLE_CLASS_ACTIONS.add);
        if (handleResize(resizeState.width + deltaOnResize, {minWidth, maxWidth})
            && deltaOnResize !== 0 && !isNaN(deltaOnResize)) {
            deltaCache.current = 0;
            setResizeState({
                ...resizeState,
                width: deltaOnResize + resizeState.width,
                deltaX: deltaOnResize
            });
        }
    };

    const handleStopResize = () => {
        toggleDocumentResizeClassName(TOGGLE_CLASS_ACTIONS.remove);
        deltaCache.current = 0;
        setStopResizeState();
    };

    return (
        <DraggableCore
            nodeRef={nodeRef}
            onStart={onStart}
            onDrag={handleDrag}
            onStop={handleStopResize}
            handle={`.${handle}`}>
            {children(resizeState, nodeRef)}
        </DraggableCore>
    );
};

            // @pb/generate-flow-typed Webpack Loader:
            // Duplicates all internal dependencies imports
            // to be sure all deps are parsed by this loader
            // cause Babel loader removes `import type ...`
            // and e.g. 'typedef.js' files are not included in dist
            
require("./typedef");
